import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  REWORK_CAUSA_LABEL,
  REWORK_PERCENTUAL_SUGERIDO,
  reworkCausaSchema,
  type ReworkCausa,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatBRL } from "@/components/prosthesis/prosthesis-meta";

type Props = {
  reworkId: string;
  serviceId: string;
  /** Preço congelado do serviço — base do percentual sugerido. Null quando o pedido nasceu sem valor. */
  precoDoServicoCents: number | null;
  causa: ReworkCausa | null;
  cobrar: boolean | null;
  valorCents: number | null;
  decididoPor: string | null;
};

const CAUSAS = reworkCausaSchema.options;

/**
 * De quem foi a causa do retrabalho e se ele será cobrado.
 *
 * A regra do laboratório: erro interno não cobra, erro da clínica cobra metade. Ela vira SUGESTÃO,
 * não automatismo — o percentual aparece preenchido e continua editável, porque o caso concreto
 * manda mais que a regra geral.
 *
 * Registra a CAUSA, não só a decisão. "Quantos retrabalhos foram erro nosso este mês?" é pergunta
 * de qualidade da bancada e vale mais no longo prazo que o valor de uma cobrança avulsa.
 */
export function DecisaoDeRetrabalho({
  reworkId,
  serviceId,
  precoDoServicoCents,
  causa,
  cobrar,
  valorCents,
  decididoPor,
}: Props) {
  const utils = trpc.useUtils();
  const jaDecidido = !!decididoPor;
  const [editando, setEditando] = useState(false);
  const [causaEscolhida, setCausaEscolhida] = useState<ReworkCausa | null>(causa);
  const [vaiCobrar, setVaiCobrar] = useState<boolean>(cobrar ?? false);
  const [reais, setReais] = useState(valorCents != null ? (valorCents / 100).toFixed(2) : "");

  const salvar = trpc.prosthesis.decidirRetrabalho.useMutation({
    onSuccess: () => {
      toast.success("Retrabalho decidido.");
      utils.prosthesis.reworkRequests.invalidate({ serviceId });
      setEditando(false);
    },
    onError: (e) => toast.error(e.message || "Erro ao registrar a decisão."),
  });

  const escolherCausa = (c: ReworkCausa) => {
    setCausaEscolhida(c);
    const percentual = REWORK_PERCENTUAL_SUGERIDO[c];
    // Causa indefinida não sugere valor de propósito — é o caso que precisa de alguém decidindo.
    if (percentual === null || precoDoServicoCents == null) return;
    setVaiCobrar(percentual > 0);
    setReais(percentual > 0 ? ((precoDoServicoCents * percentual) / 100 / 100).toFixed(2) : "");
  };

  if (jaDecidido && !editando) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-red-200/60 pt-2 dark:border-red-900/40">
        <span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-zinc-700 dark:bg-card dark:text-foreground">
          {causa ? REWORK_CAUSA_LABEL[causa] : "Causa não registrada"}
        </span>
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[11px] font-semibold",
            cobrar
              ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
              : "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
          )}
        >
          {cobrar ? `Cobrar ${valorCents != null ? formatBRL(valorCents) : ""}`.trim() : "Sem cobrança"}
        </span>
        <button
          type="button"
          onClick={() => setEditando(true)}
          className="text-[11px] font-medium text-brand-600 hover:underline dark:text-primary"
        >
          Rever
        </button>
      </div>
    );
  }

  const centavos = Math.round(Number(reais.replace(",", ".")) * 100);
  const valorInvalido = vaiCobrar && (!Number.isFinite(centavos) || centavos <= 0);

  return (
    <div className="mt-2 space-y-2 border-t border-red-200/60 pt-2 dark:border-red-900/40">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        De quem foi a causa?
      </p>
      <div className="flex flex-wrap gap-1.5">
        {CAUSAS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => escolherCausa(c)}
            aria-pressed={causaEscolhida === c}
            className={cn(
              "inline-flex h-8 items-center rounded-md border px-2.5 text-[11.5px] font-medium transition-colors",
              causaEscolhida === c
                ? "border-brand-600 bg-brand-600 font-semibold text-white"
                : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 dark:border-border dark:bg-card dark:text-muted-foreground",
            )}
          >
            {REWORK_CAUSA_LABEL[c]}
          </button>
        ))}
      </div>

      {causaEscolhida && (
        <>
          <label className="flex items-center gap-2 text-[12.5px] text-zinc-700 dark:text-foreground/80">
            <input
              type="checkbox"
              checked={vaiCobrar}
              onChange={(e) => setVaiCobrar(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
            />
            Cobrar este retrabalho
          </label>

          {vaiCobrar && (
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">R$</span>
              <input
                inputMode="decimal"
                value={reais}
                onChange={(e) => setReais(e.target.value)}
                placeholder="0,00"
                className="h-9 w-[120px] rounded-md border border-zinc-200 bg-white px-2 text-[13px] text-zinc-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
              />
              {precoDoServicoCents != null && (
                <span className="text-[11px] text-zinc-400 dark:text-muted-foreground/80">
                  serviço: {formatBRL(precoDoServicoCents)}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={salvar.isPending || valorInvalido}
              onClick={() =>
                salvar.mutate({
                  reworkId,
                  causa: causaEscolhida,
                  cobrar: vaiCobrar,
                  valorCents: vaiCobrar ? centavos : null,
                })
              }
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Registrar decisão
            </button>
            {jaDecidido && (
              <button
                type="button"
                onClick={() => setEditando(false)}
                className="inline-flex h-9 items-center rounded-md px-2.5 text-[12.5px] font-medium text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted"
              >
                Cancelar
              </button>
            )}
          </div>

          <p className="text-[11px] text-zinc-400 dark:text-muted-foreground/80">
            Fica registrado com autor e data. O valor é congelado agora — mudar a tabela de preços
            depois não altera esta cobrança.
          </p>
        </>
      )}
    </div>
  );
}
